import { cn } from "@/ui/utils/cn";
import { ChevronDown } from "lucide-react";

type AccordionBody =
  | {
      answer: string;
      /** Numbered steps shown under the answer, for the "how" kind of question. */
      steps?: string[];
    }
  /** A rendered markdown article (the help centre's), shown as the answer. */
  | { html: string };

export type AccordionItem = { id: string; question: string } & AccordionBody;

/**
 * Native <details> accordion: keyboard accessible, no JS needed, and the
 * answer text stays in the DOM for search engines.
 */
export const Accordion = ({
  items,
  className,
}: {
  items: AccordionItem[];
  className?: string;
}) => (
  <div
    className={cn(
      "divide-y divide-slate-200 border-y border-slate-200",
      className,
    )}
  >
    {items.map((item) => (
      <details key={item.id} className="group py-1">
        <summary className="flex cursor-pointer list-none items-center justify-between gap-6 py-4 text-start text-[17px] font-semibold text-ink marker:content-none [&::-webkit-details-marker]:hidden">
          {item.question}
          <ChevronDown
            size={20}
            className="shrink-0 text-slate-400 transition-transform group-open:rotate-180"
            aria-hidden="true"
          />
        </summary>
        {"html" in item ? (
          <div
            className="contentlayer contentlayer-inline max-w-prose pb-5 text-body text-slate-600"
            dangerouslySetInnerHTML={{ __html: item.html }}
          />
        ) : (
          <div className="max-w-prose pb-5 text-body text-slate-600">
            <p>{item.answer}</p>
            {item.steps ? (
              <ol className="mt-3 list-decimal space-y-2 ps-5 marker:font-semibold marker:text-ink">
                {item.steps.map((step) => (
                  <li key={step} className="ps-1">
                    {step}
                  </li>
                ))}
              </ol>
            ) : null}
          </div>
        )}
      </details>
    ))}
  </div>
);
